/**
 * Administração (E10a — integracao-convenia §4.8): RESET GERAL do sistema.
 * Operação de RECOMEÇO (pré-operação real / troca de fase), nunca de rotina:
 * exige digitar "RESET", cria backup datado OBRIGATÓRIO antes de tocar no banco
 * (falha no backup aborta tudo) e re-aplica só os seeds mínimos — o sync
 * Convenia repovoa os cadastros. A lista do que NÃO volta fica explícita na
 * tela (decisão do dono 27/07/2026).
 */
import { useState } from "react";
import { Alert, Button, Card, Code, Group, List, Text, TextInput, Title } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { api } from "../api";
import { notificarErroApi } from "../componentes/notificacoes";
import { ConfigIaCard } from "../ia-config";

const NAO_VOLTA = [
  "Lançamentos, runs e itens calculados",
  "Competências (inclusive as fechadas) e snapshots",
  "Retornos da Contmega e divergências de conferência",
  "Tomadores e NFs de retenção",
  "Empréstimos/adiantamentos",
  "De-para de ponto e espelhos importados",
  "Fotos dos cadastros",
  "Todo o histórico de auditoria (fica só DENTRO do backup)",
];

const SEEDS_MINIMOS = [
  "Parâmetros 2026 (INSS, IRRF, FGTS, VT/VR…)",
  "Catálogo de eventos e fórmulas",
  "Calendários 2026–01/2027 e feriados",
  "Jornadas e tipos de benefício",
  "O token do Convenia (convenia_config) é preservado",
];

export function AdminPage() {
  const [digitado, setDigitado] = useState("");
  const [rodando, setRodando] = useState(false);
  const [resultado, setResultado] = useState<{ backup: string; aviso: string } | null>(null);

  async function executarReset() {
    try {
      setRodando(true);
      setResultado(null);
      const r = await api.post<{ backup: string; aviso: string }>("/admin/reset-geral", { confirmacao: digitado });
      setResultado(r);
      setDigitado("");
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setRodando(false);
    }
  }

  return (
    <>
      <Title order={2} mb={4}>Administração</Title>
      <Text size="sm" c="dimmed" mb="md">
        Operações de sistema — uso excepcional.
      </Text>

      {/* Camada de IA (03/09/2026) — chave OpenRouter + modelo padrão */}
      <ConfigIaCard />

      <Card withBorder shadow="xs" mb="md" style={{ borderColor: "var(--mantine-color-red-4)" }}>
        <Group gap="sm" mb="sm">
          <IconAlertTriangle size={20} color="var(--mantine-color-red-7)" />
          <Title order={4} c="red.7">Reset geral do sistema</Title>
        </Group>
        <Text size="sm" mb="sm">
          Zera TODA a base e re-aplica apenas os seeds mínimos — operação de recomeço (pré-operação real ou troca de
          fase), nunca de rotina. Um <strong>backup datado é criado ANTES</strong> (se o backup falhar, nada é
          apagado) e os cadastros são repovoados depois pelo <strong>sync Convenia</strong>.
        </Text>
        <Group align="flex-start" gap="xl" mb="md" wrap="wrap">
          <div>
            <Text size="sm" fw={700} c="red.7" mb={4}>O que NÃO volta (só existe no backup):</Text>
            <List size="sm" spacing={2}>
              {NAO_VOLTA.map((x) => (
                <List.Item key={x}>{x}</List.Item>
              ))}
            </List>
          </div>
          <div>
            <Text size="sm" fw={700} mb={4}>O que é recriado na hora (seeds mínimos):</Text>
            <List size="sm" spacing={2}>
              {SEEDS_MINIMOS.map((x) => (
                <List.Item key={x}>{x}</List.Item>
              ))}
            </List>
          </div>
        </Group>
        <Group align="flex-end" wrap="wrap">
          <TextInput
            label='Digite RESET para habilitar'
            placeholder="RESET"
            value={digitado}
            onChange={(e) => setDigitado(e.currentTarget.value)}
            w={200}
            autoComplete="off"
          />
          <Button
            color="red"
            disabled={digitado !== "RESET"}
            loading={rodando}
            onClick={() => void executarReset()}
          >
            Executar reset geral
          </Button>
        </Group>
        {resultado && (
          <Alert color="green" title="Reset concluído" mt="md">
            <Text size="sm" mb={4}>{resultado.aviso}</Text>
            <Text size="sm">
              Backup pré-reset em: <Code>{resultado.backup}</Code>
            </Text>
            <Text size="sm" mt={4}>
              Próximo passo: tela <strong>Integração Convenia → Atualizar tudo</strong> para repovoar os cadastros.
            </Text>
          </Alert>
        )}
      </Card>
    </>
  );
}
